import OpenSeadragon from 'openseadragon'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { fetchPublicAssetDeepZoomDerivative, resolvePublicApiAbsoluteUrl, type PublicDeepZoomDerivative, type PublicGalleryImage } from './publicApi'

/**
 * The above-the-fold, full-viewport Deep Zoom "viewer-stage" — ported 1:1
 * from Legacy's `static/js/viewer.js` + `static/css/viewer.css`
 * (`G:\LiangCollection\backend\static\{js\viewer.js,css\viewer.css}`,
 * source-audited for this round). Legacy's own DOM ids/classes
 * (`viewer-stage`/`viewer-osd`/`viewer-top-bar`/`viewer-controls`/
 * `viewer-btn`/`viewer-thumb-track`/`thumb-list`/`thumb-item`/
 * `viewer-counter`) are reused verbatim in `public.css` so this component
 * is a behavioral+visual port, not a redesign.
 *
 * Storage-neutral by construction, same rule as the admin `DeepZoomViewer`:
 * this component only ever calls `GET .../assets/{id}/derivatives/deepzoom`
 * (the *public*, unauthenticated counterpart route) and builds tile URLs
 * from the `tiles_base_url` that endpoint returns — it never knows or
 * cares whether the backend's actual bytes live on local disk or R2.
 *
 * One deliberate simplification vs. Legacy: Legacy hand-rolls a
 * requestAnimationFrame cubic-easing loop (`animateViewport`) for the
 * immersive/fit view-mode transition; this port instead uses
 * OpenSeadragon's own built-in spring-animated `zoomTo`/`panTo`
 * (`immediately=false`), which is visually equivalent (smooth eased
 * zoom+pan) without re-implementing an animation loop OpenSeadragon
 * already provides.
 */

type ViewMode = 'immersive' | 'fit'

const AUTO_HIDE_MS = 3000

export default function ImmersiveDeepZoomStage({
  workspaceSlug,
  siteSlug,
  images,
  imageInitializing = false,
  title,
  secondaryTitle,
  backHref,
  backLabel = '返回条目列表',
}: {
  workspaceSlug: string
  siteSlug: string
  images: PublicGalleryImage[]
  /** ITEM-DETAIL-INITIAL-IMAGE-RACE-P0: `images` is empty AND a browser
   * upload for this item's first image is still genuinely in flight
   * (never true for an item that simply has no image — see
   * `PublicItemDetailPage`'s `imageInitializing` computation). Renders a
   * short-lived "preparing" indicator instead of the permanent "no
   * image" empty state. */
  imageInitializing?: boolean
  title: string
  secondaryTitle?: string | null
  backHref?: string
  backLabel?: string
}) {
  const stageRef = useRef<HTMLDivElement>(null)
  const osdRef = useRef<HTMLDivElement>(null)
  const controlsRef = useRef<HTMLDivElement>(null)
  const thumbTrackRef = useRef<HTMLDivElement>(null)
  const viewerRef = useRef<OpenSeadragon.Viewer | null>(null)
  const viewModeRef = useRef<ViewMode>('immersive')
  const hideTimerRef = useRef<number | undefined>(undefined)
  const suppressHideRef = useRef(false)
  const lastRequestedAssetIdRef = useRef<string | null>(null)

  const [searchParams, setSearchParams] = useSearchParams()
  const initialIndex = (() => {
    const raw = parseInt(searchParams.get('image') || '1', 10) - 1
    return Number.isFinite(raw) ? Math.max(0, Math.min(images.length - 1, raw || 0)) : 0
  })()
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [derivative, setDerivative] = useState<PublicDeepZoomDerivative | null>(null)
  const [derivativeError, setDerivativeError] = useState<string | null>(null)
  const [controlsVisible, setControlsVisible] = useState(true)
  const [zoomState, setZoomState] = useState({ canZoomIn: true, canZoomOut: false })
  const [viewModeState, setViewModeState] = useState<ViewMode>('immersive')
  const [isFullscreen, setIsFullscreen] = useState(false)
  // Genuinely-missing-image guard ONLY — a real Asset URL contract fix
  // must make this set stay empty for any item that actually has image
  // data (see ITEM-DETAIL-IMAGE-P0). Keyed by the exact `src` string so a
  // later successful load of the *same* URL (e.g. after a retry/redeploy)
  // is never permanently masked by a stale entry.
  const [brokenImageSrcs, setBrokenImageSrcs] = useState<Set<string>>(new Set())
  const markImageBroken = useCallback((src: string | null | undefined) => {
    if (!src) return
    setBrokenImageSrcs((prev) => (prev.has(src) ? prev : new Set(prev).add(src)))
  }, [])

  const currentImage = images[currentIndex] as PublicGalleryImage | undefined

  // ---------------------------------------------------------- Auto-hide
  const showControls = useCallback(() => setControlsVisible(true), [])
  const scheduleHide = useCallback(() => {
    if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current)
    hideTimerRef.current = window.setTimeout(() => {
      if (!suppressHideRef.current) setControlsVisible(false)
    }, AUTO_HIDE_MS)
  }, [])
  const resetAutoHide = useCallback(() => {
    showControls()
    scheduleHide()
  }, [showControls, scheduleHide])

  useEffect(() => {
    resetAutoHide()
    return () => {
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const el = document.fullscreenElement
    setIsFullscreen(!!el)
    const handler = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', handler)
    return () => document.removeEventListener('fullscreenchange', handler)
  }, [])

  // -------------------------------------------------- View-mode geometry
  const getHomeZoom = useCallback(() => viewerRef.current?.viewport.getHomeZoom() ?? 1, [])
  const getCoverZoom = useCallback(() => {
    const viewer = viewerRef.current
    const home = getHomeZoom()
    if (!viewer || !viewer.world.getItemCount()) return home
    const bounds = viewer.world.getItemAt(0).getBounds()
    const container = viewer.viewport.getContainerSize()
    if (!container || !bounds.width || !bounds.height) return home
    const containerAspect = container.x / container.y
    const imageAspect = bounds.width / bounds.height
    return containerAspect > imageAspect ? home * (containerAspect / imageAspect) : home * (imageAspect / containerAspect)
  }, [getHomeZoom])
  const getMinZoomForMode = useCallback((mode: ViewMode) => (mode === 'immersive' ? getCoverZoom() : getHomeZoom()), [getCoverZoom, getHomeZoom])
  const getImageCenter = useCallback(() => {
    const viewer = viewerRef.current
    if (!viewer || !viewer.world.getItemCount()) return null
    return viewer.world.getItemAt(0).getBounds().getCenter()
  }, [])

  const updateZoomButtonState = useCallback(() => {
    const viewer = viewerRef.current
    if (!viewer) return
    const zoom = viewer.viewport.getZoom()
    const minZoom = getMinZoomForMode(viewModeRef.current)
    const max = viewer.viewport.getMaxZoom()
    setZoomState({ canZoomIn: zoom < max * 0.999, canZoomOut: zoom > minZoom * 1.001 })
  }, [getMinZoomForMode])

  const applyViewMode = useCallback(
    (mode: ViewMode, immediately: boolean) => {
      const viewer = viewerRef.current
      if (!viewer) return
      const center = getImageCenter() ?? viewer.viewport.getCenter()
      viewModeRef.current = mode
      setViewModeState(mode)
      // `minZoomLevel`/`visibilityRatio` are real, documented, mutable
      // OpenSeadragon.Viewport runtime properties; the `openseadragon`
      // npm package's own .d.ts just doesn't declare them as writable.
      const viewport = viewer.viewport as OpenSeadragon.Viewport & { minZoomLevel: number; visibilityRatio: number }
      if (mode === 'immersive') {
        viewport.minZoomLevel = getCoverZoom() * 0.995
        viewport.visibilityRatio = 0
      } else {
        viewport.minZoomLevel = getHomeZoom()
        viewport.visibilityRatio = 1
      }
      const targetZoom = getMinZoomForMode(mode)
      viewer.viewport.setRotation(0, immediately)
      viewer.viewport.zoomTo(targetZoom, undefined, immediately)
      viewer.viewport.panTo(center, immediately)
      viewer.viewport.applyConstraints(immediately)
      updateZoomButtonState()
    },
    [getCoverZoom, getHomeZoom, getImageCenter, getMinZoomForMode, updateZoomButtonState],
  )

  const toggleViewMode = useCallback(() => {
    applyViewMode(viewModeRef.current === 'immersive' ? 'fit' : 'immersive', false)
    resetAutoHide()
  }, [applyViewMode, resetAutoHide])

  const zoomIn = useCallback(() => {
    const viewer = viewerRef.current
    if (!viewer) return
    const current = viewer.viewport.getZoom()
    const max = viewer.viewport.getMaxZoom()
    const target = Math.min(current * 1.3, max)
    if (target > current) {
      viewer.viewport.zoomTo(target, undefined, false)
      viewer.viewport.applyConstraints(false)
    }
    resetAutoHide()
  }, [resetAutoHide])

  const zoomOut = useCallback(() => {
    const viewer = viewerRef.current
    if (!viewer) return
    const current = viewer.viewport.getZoom()
    const minZoom = getMinZoomForMode(viewModeRef.current)
    if (current <= minZoom * 1.001) {
      applyViewMode(viewModeRef.current, false)
      resetAutoHide()
      return
    }
    const target = current * 0.7
    if (target <= minZoom * 1.03) {
      applyViewMode(viewModeRef.current, false)
    } else {
      const imageCenter = getImageCenter()
      const currentCenter = viewer.viewport.getCenter()
      if (imageCenter) {
        const ratio = (target - minZoom) / (current - minZoom)
        viewer.viewport.zoomTo(target, undefined, false)
        viewer.viewport.panTo(
          new OpenSeadragon.Point(
            imageCenter.x + (currentCenter.x - imageCenter.x) * ratio,
            imageCenter.y + (currentCenter.y - imageCenter.y) * ratio,
          ),
          false,
        )
      } else {
        viewer.viewport.zoomTo(target, undefined, false)
      }
      viewer.viewport.applyConstraints(false)
    }
    resetAutoHide()
  }, [applyViewMode, getImageCenter, getMinZoomForMode, resetAutoHide])

  const rotate = useCallback(() => {
    const viewer = viewerRef.current
    if (!viewer) return
    viewer.viewport.setRotation((viewer.viewport.getRotation() + 90) % 360)
    viewer.viewport.applyConstraints(true)
    resetAutoHide()
  }, [resetAutoHide])

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      stageRef.current?.requestFullscreen().catch(() => {})
    } else {
      document.exitFullscreen().catch(() => {})
    }
    resetAutoHide()
  }, [resetAutoHide])

  // ------------------------------------------------------- OSD lifecycle
  useEffect(() => {
    if (!osdRef.current) return
    const viewer = OpenSeadragon({
      element: osdRef.current,
      showNavigationControl: false,
      showZoomControl: false,
      showHomeControl: false,
      showFullPageControl: false,
      showRotationControl: false,
      maxZoomPixelRatio: 10,
      minZoomImageRatio: 1,
      visibilityRatio: 0,
      constrainDuringPan: true,
      // No `crossOriginPolicy` (Legacy's own viewer.js sets 'Anonymous',
      // but that assumes Legacy's R2 bucket's CORS policy already allows
      // this origin — Vistoera's own dev R2 bucket doesn't yet, and
      // forcing `crossorigin="anonymous"` on every tile/fallback <img>
      // makes the browser reject an otherwise-perfectly-loadable same-
      // origin-proxied-but-R2-redirected image whose response lacks
      // `Access-Control-Allow-Origin`. The admin `DeepZoomViewer` never
      // sets this either, for the same reason — plain <img> display
      // (unlike canvas pixel readback) never needs it.)
      animationTime: 0.5,
      gestureSettingsMouse: {
        scrollToZoom: false,
        clickToZoom: false,
        dblClickToZoom: true,
        dragToPan: true,
        pinchToZoom: false,
        flickEnabled: false,
      },
      gestureSettingsTouch: { pinchToZoom: true, dragToPan: true, clickToZoom: false, dblClickToZoom: false, flickEnabled: false },
    })
    viewerRef.current = viewer
    // Legacy neutralizes OSD's own wheel handler entirely (`viewer.js`:
    // "Ensure wheel events never zoom even if gesture settings are
    // re-applied") so the wheel event keeps bubbling to the document and
    // scrolls the *page* down to `.details-section` below the fold —
    // `gestureSettingsMouse.scrollToZoom: false` alone still lets
    // OpenSeadragon's internal MouseTracker intercept/stop the wheel
    // event on the canvas, which silently ate page-scroll wheel input.
    const innerTracker = (viewer as unknown as { innerTracker?: { scrollHandler?: unknown } }).innerTracker
    if (innerTracker) innerTracker.scrollHandler = () => {}

    viewer.addHandler('open', () => {
      viewModeRef.current = 'immersive'
      applyViewMode('immersive', true)
    })
    viewer.addHandler('zoom', () => {
      viewer.viewport.applyConstraints(true)
      updateZoomButtonState()
      resetAutoHide()
    })
    viewer.addHandler('pan', () => resetAutoHide())
    viewer.addHandler('resize', () => {
      if (!viewer.world.getItemCount()) return
      applyViewMode(viewModeRef.current, true)
    })

    return () => {
      viewer.destroy()
      viewerRef.current = null
    }
    // Intentionally mount/destroy the OpenSeadragon instance exactly once
    // per stage lifetime (Legacy parity: a single persistent viewer,
    // `viewer.open()` is what switches images — never recreate the whole
    // viewer per image, which would flash/reset zoom-mode state).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // --------------------------------------------- Per-image derivative fetch
  useEffect(() => {
    const img = currentImage
    setDerivative(null)
    setDerivativeError(null)
    if (!img) return
    let cancelled = false
    let timer: number | undefined
    lastRequestedAssetIdRef.current = img.asset_id

    const poll = () => {
      fetchPublicAssetDeepZoomDerivative(workspaceSlug, siteSlug, img.asset_id)
        .then((r) => {
          if (cancelled) return
          setDerivative(r.data)
          if (r.data.status === 'pending' || r.data.status === 'processing') {
            timer = window.setTimeout(poll, 2000)
          }
        })
        .catch((err) => {
          if (cancelled) return
          setDerivativeError((err as { message?: string })?.message ?? '高清切片状态查询失败')
        })
    }
    poll()
    return () => {
      cancelled = true
      if (timer) window.clearTimeout(timer)
    }
  }, [currentImage, workspaceSlug, siteSlug])

  // --------------------------------------------------- Open tile source
  useEffect(() => {
    const viewer = viewerRef.current
    const img = currentImage
    if (!viewer || !img) return
    // A poll response for a since-abandoned image switch must never open
    // into the (now stale) viewer.
    if (derivative && lastRequestedAssetIdRef.current !== img.asset_id) return

    const ready = derivative?.status === 'ready' && !!derivative.tiles_base_url && !!derivative.width && !!derivative.height

    // Same permissive cast the admin `DeepZoomViewer` already uses for
    // this custom-tile-URL-template TileSource shape (`openseadragon`'s
    // own .d.ts models the stricter `dzi`/IIIF-preset shapes, not this
    // one) — `viewer.open()`'s parameter type is stricter still than the
    // constructor's `tileSources` option, hence the extra `as any`.
    if (ready) {
      const tilesBaseUrl = resolvePublicApiAbsoluteUrl(derivative!.tiles_base_url!)
      const format = derivative!.format ?? 'webp'
      const maxLevel = Math.max(0, (derivative!.levels ?? 1) - 1)
      const open = viewer.open.bind(viewer) as (source: unknown) => OpenSeadragon.Viewer
      open({
        width: derivative!.width!,
        height: derivative!.height!,
        tileSize: derivative!.tile_size ?? 512,
        tileOverlap: derivative!.overlap ?? 1,
        minLevel: 0,
        maxLevel,
        getTileUrl: (level: number, x: number, y: number) => `${tilesBaseUrl}${level}/${x}_${y}.${format}`,
      })
    } else {
      // Deliberately never fed to OpenSeadragon as a `type: 'image'`
      // TileSource here (unlike the `ready` branch above): its default
      // `buildPyramid: true` draws the image onto an offscreen <canvas>
      // and reads it back to build downsampled mip levels, and — same as
      // the WebGLDrawer's own per-tile texture upload — that throws the
      // moment the image is cross-origin without CORS headers, exactly
      // our R2-redirected, no-`Access-Control-Allow-Origin` case (DZI
      // tiles hit an internal WebGL→Canvas fallback when *they* draw
      // tainted data; this single-image path doesn't reliably get the
      // same fallback and silently renders nothing). The plain <img>
      // fallback overlay rendered below `.viewer-osd` — the same
      // approach the working admin `DeepZoomViewer` already uses — has
      // no such restriction: 2D `<img>` display never needs pixel
      // readback, only canvas/WebGL texture use does.
      viewer.close()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentImage, derivative?.status, derivative?.tiles_base_url, derivative?.width, derivative?.height])

  // ----------------------------------------------------------- Navigation
  const switchTo = useCallback(
    (index: number) => {
      if (index < 0 || index >= images.length || index === currentIndex) return
      setCurrentIndex(index)
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (index === 0) next.delete('image')
          else next.set('image', String(index + 1))
          return next
        },
        { replace: true },
      )
      resetAutoHide()
    },
    [currentIndex, images.length, resetAutoHide, setSearchParams],
  )

  // --------------------------------------------------------- Keyboard
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return
      switch (e.key) {
        case 'ArrowLeft':
          switchTo(currentIndex - 1)
          break
        case 'ArrowRight':
          switchTo(currentIndex + 1)
          break
        case '+':
        case '=':
          zoomIn()
          break
        case '-':
        case '_':
          zoomOut()
          break
        case '0':
          toggleViewMode()
          break
        case 'f':
        case 'F':
          toggleFullscreen()
          break
        case 'r':
        case 'R':
          rotate()
          break
        case 'Escape':
          if (document.fullscreenElement) document.exitFullscreen().catch(() => {})
          else showControls()
          break
        default:
          return
      }
      resetAutoHide()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [currentIndex, rotate, resetAutoHide, showControls, switchTo, toggleFullscreen, toggleViewMode, zoomIn, zoomOut])

  if (!images.length) {
    return (
      <section className="viewer-stage">
        <div className="viewer-osd">
          {imageInitializing ? (
            <div className="viewer-image-initializing" role="status" aria-live="polite">
              <span className="viewer-spinner" aria-hidden="true" />
              <span>图片正在准备中…</span>
            </div>
          ) : (
            <div className="no-image">暂无图片</div>
          )}
        </div>
      </section>
    )
  }

  const showingFallback = !(derivative?.status === 'ready' && derivative.tiles_base_url && derivative.width && derivative.height)
  const fallbackImageUrl = currentImage ? currentImage.url ?? currentImage.thumbnail_url ?? currentImage.original_url ?? null : null
  const fallbackBannerText =
    derivativeError ??
    (derivative?.status === 'failed'
      ? `高清切片生成失败${derivative.error_message ? `：${derivative.error_message}` : ''}，当前使用缩略图预览。`
      : derivative?.status === 'pending' || derivative?.status === 'processing'
        ? '高清切片正在生成中，当前使用缩略图预览…'
        : null)

  return (
    <section
      className={`viewer-stage${isFullscreen ? ' is-fullscreen' : ''}`}
      ref={stageRef}
      style={{ cursor: !controlsVisible && isFullscreen ? 'none' : 'default' }}
      // Capture phase, not bubble: OpenSeadragon's own MouseTracker
      // attaches bubble-phase listeners on `.openseadragon-canvas` and
      // stops their propagation, so a bubble-phase listener up here would
      // never see a mousemove/click/wheel that started on the canvas —
      // exactly where the user's cursor spends most of its time.
      onMouseMoveCapture={resetAutoHide}
      onClickCapture={resetAutoHide}
      onTouchStartCapture={resetAutoHide}
      onWheelCapture={resetAutoHide}
    >
      <div className={`viewer-top-bar${controlsVisible ? '' : ' hidden'}`}>
        {backHref && (
          <Link className="viewer-back" to={backHref} onClick={(e) => e.stopPropagation()}>
            ← {backLabel}
          </Link>
        )}
        <div className="viewer-title">
          <h1>{title}</h1>
          {secondaryTitle && <span className="original-title">{secondaryTitle}</span>}
        </div>
      </div>

      <div className="viewer-osd" ref={osdRef}>
        {showingFallback && fallbackImageUrl && !brokenImageSrcs.has(fallbackImageUrl) && (
          <img
            className="viewer-preview-fallback-image"
            src={fallbackImageUrl}
            alt={title}
            onError={() => markImageBroken(fallbackImageUrl)}
          />
        )}
        {showingFallback && fallbackImageUrl && brokenImageSrcs.has(fallbackImageUrl) && (
          <div className="no-image">该图片暂时无法加载。</div>
        )}
        {showingFallback && fallbackBannerText && <div className="viewer-preview-fallback-banner">{fallbackBannerText}</div>}
      </div>

      <div
        className={`viewer-controls${controlsVisible ? '' : ' hidden'}`}
        ref={controlsRef}
        onMouseEnter={() => {
          suppressHideRef.current = true
          if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current)
        }}
        onMouseLeave={() => {
          suppressHideRef.current = false
          scheduleHide()
        }}
      >
        <button type="button" className={`viewer-btn${currentIndex === 0 ? ' disabled' : ''}`} aria-label="上一张" disabled={currentIndex === 0} onClick={() => switchTo(currentIndex - 1)}>
          ‹
        </button>
        <button type="button" className={`viewer-btn${currentIndex === images.length - 1 ? ' disabled' : ''}`} aria-label="下一张" disabled={currentIndex === images.length - 1} onClick={() => switchTo(currentIndex + 1)}>
          ›
        </button>
        <button
          type="button"
          className={`viewer-btn${!zoomState.canZoomIn || showingFallback ? ' disabled' : ''}`}
          aria-label="放大"
          disabled={!zoomState.canZoomIn || showingFallback}
          onClick={zoomIn}
        >
          +
        </button>
        <button
          type="button"
          className={`viewer-btn${!zoomState.canZoomOut || showingFallback ? ' disabled' : ''}`}
          aria-label="缩小"
          disabled={!zoomState.canZoomOut || showingFallback}
          onClick={zoomOut}
        >
          −
        </button>
        <button
          type="button"
          className={`viewer-btn${viewModeState === 'fit' ? ' view-mode-fit' : ''}${showingFallback ? ' disabled' : ''}`}
          aria-label={viewModeState === 'fit' ? '沉浸式高清浏览' : '完整作品'}
          title={viewModeState === 'fit' ? '恢复沉浸式高清浏览' : '查看完整作品'}
          disabled={showingFallback}
          onClick={toggleViewMode}
        >
          ⌂
        </button>
        <button type="button" className={`viewer-btn${showingFallback ? ' disabled' : ''}`} aria-label="旋转" title="旋转 90°" disabled={showingFallback} onClick={rotate}>
          ↻
        </button>
        <button type="button" className="viewer-btn" aria-label="全屏" title="全屏" onClick={toggleFullscreen}>
          ⛶
        </button>
      </div>

      {images.length > 1 && (
        <div
          className={`viewer-thumb-track${controlsVisible ? '' : ' hidden'}`}
          ref={thumbTrackRef}
          onMouseEnter={() => {
            suppressHideRef.current = true
            if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current)
          }}
          onMouseLeave={() => {
            suppressHideRef.current = false
            scheduleHide()
          }}
        >
          <div className="thumb-list">
            {images.map((img, index) => {
              const thumbSrc = img.thumbnail_url ?? img.url ?? undefined
              const isBroken = !!thumbSrc && brokenImageSrcs.has(thumbSrc)
              return (
                <div
                  key={img.asset_id}
                  className={`thumb-item${index === currentIndex ? ' active' : ''}${isBroken ? ' thumb-item-broken' : ''}`}
                  role="button"
                  aria-label={`第 ${index + 1} 张图片`}
                  onClick={() => switchTo(index)}
                >
                  {thumbSrc && (
                    <img src={thumbSrc} alt={img.caption || ''} loading="lazy" onError={() => markImageBroken(thumbSrc)} />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      <div className={`viewer-counter${controlsVisible ? '' : ' hidden'}`}>
        {currentIndex + 1} / {images.length}
      </div>
    </section>
  )
}
